/* eslint-disable eqeqeq */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  clearAuthSession,
  getPostLoginPath,
  hasValidAuthSession,
  normalizeAdminLoginPayload,
  persistAuthSession,
  resolveClubStaffResumePath,
  resolveLoginRole,
} from "../../../helpers/authSession";
import { isLikelyJwt } from "../../../helpers/authToken";
import { loginAdmin } from "../../../store/features/auth/authSlice";
import { showToast } from "../../../store/features/toast/toastSlice";
import Constant from "../../../util/constant";
import LoginComponent from "./loginComponent";

const LoginContainer = (props) => {
  const [busy, setBusy] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loginForm = {
    email: {
      name: "email",
      validate: {
        required: {
          value: true,
          message: "Email Adress Required",
        },
        pattern: {
          value: Constant.REGEX.EMAIL,
          message: "Please enter a valid email address",
        },
      },
    },
    password: {
      name: "password",
      validate: {
        required: {
          value: true,
          message: "Please enter a password",
        },
      },
    },
  };

  useEffect(() => {
    if (
      window.sessionStorage.getItem(Constant.SESSIONSTORAGEKEYS.REMEMBER_ME) ===
      "true"
    ) {
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (!hasValidAuthSession()) return;
    const role = window.localStorage.getItem("role");
    let path = getPostLoginPath(role);
    if (
      role === Constant.ROLES.CLUB_MANAGER ||
      role === Constant.ROLES.CLUB_EMPLOYEE
    ) {
      path = resolveClubStaffResumePath(role);
    }
    if (path) {
      navigate(path, { replace: true });
    }
  }, [navigate]);

  const onFormSubmit = async (value) => {
    const email = (value?.email ?? "").trim();
    const password = value?.password ?? "";
    const credentials = { email, password };
    setBusy(true);
    try {
      const resultAction = await dispatch(loginAdmin(credentials));
      if (loginAdmin.fulfilled.match(resultAction)) {
        const normalized = normalizeAdminLoginPayload(resultAction.payload);
        const role = resolveLoginRole(normalized);

        if (!normalized.accessToken) {
          dispatch(
            showToast({
              show: false,
              message: "Login succeeded but no access token was returned.",
              type: "error",
            })
          );
          return;
        }

        if (!isLikelyJwt(normalized.accessToken)) {
          dispatch(
            showToast({
              show: false,
              message:
                "Login succeeded but the access token from the server was invalid. Check the login API response.",
              type: "error",
            })
          );
          return;
        }

        persistAuthSession({
          accessToken: normalized.accessToken,
          refreshToken: normalized.refreshToken,
          user: normalized.user,
          role,
          rememberMe,
          email,
          password,
        });

        dispatch(
          showToast({
            show: false,
            message: normalized.message || "Login successful",
            type: "success",
          })
        );

        const ticketsReturn = window.sessionStorage.getItem(
          Constant.SESSIONSTORAGEKEYS.MY_TICKETS_REDIRECT
        );
        if (
          ticketsReturn &&
          String(ticketsReturn).startsWith("/") &&
          String(ticketsReturn).includes("/my-tickets/")
        ) {
          window.sessionStorage.removeItem(
            Constant.SESSIONSTORAGEKEYS.MY_TICKETS_REDIRECT
          );
          navigate(ticketsReturn, { replace: true });
          return;
        }

        const path = getPostLoginPath(role);
        const destination =
          role === Constant.ROLES.CLUB_MANAGER ||
          role === Constant.ROLES.CLUB_EMPLOYEE
            ? resolveClubStaffResumePath(role)
            : path;
        if (destination) {
          navigate(destination, { replace: true });
          return;
        }

        dispatch(
          showToast({
            show: false,
            message:
              "Login succeeded but your account role is not allowed in this portal.",
            type: "error",
          })
        );
        await clearAuthSession(dispatch);
        return;
      }

      if (loginAdmin.rejected.match(resultAction)) {
        const apiMessage =
          (typeof resultAction.payload === "string" && resultAction.payload) ||
          resultAction?.error?.message ||
          "Login failed";
        dispatch(
          showToast({
            show: false,
            message: apiMessage,
            type: "error",
          })
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <LoginComponent
      rememberMe={rememberMe}
      setRememberMe={setRememberMe}
      loginForm={loginForm}
      onFormSubmit={onFormSubmit}
      showPass={showPass}
      setShowPass={setShowPass}
      busy={busy}
    />
  );
};

export default LoginContainer;
